import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations";
import {
  getWallet,
  getWalletTransactions,
  getWalletTransaction,
  initiateTopUp,
  getTopUpStatus,
} from "../infrastructure/wallet.api";

export function useWallet() {
  const org = useCurrentOrganization();

  return useQuery({
    queryKey: ["wallet", org?.id],
    queryFn: () => getWallet(org!.id),
    enabled: !!org?.id,
    staleTime: 30_000,
  });
}

export function useWalletTransactions(page = 1, pageSize = 20) {
  const org = useCurrentOrganization();

  return useQuery({
    queryKey: ["wallet", "transactions", org?.id, page, pageSize],
    queryFn: () => getWalletTransactions(org!.id, page, pageSize),
    enabled: !!org?.id,
  });
}

export function useWalletTransaction(transactionId: string | undefined) {
  const org = useCurrentOrganization();

  return useQuery({
    queryKey: ["wallet", "transaction", org?.id, transactionId],
    queryFn: () => getWalletTransaction(org!.id, transactionId!),
    enabled: !!org?.id && !!transactionId,
  });
}

export function useInitiateTopUp() {
  const queryClient = useQueryClient();
  const org = useCurrentOrganization();

  return useMutation({
    mutationFn: (amount: number) =>
      initiateTopUp({ organizationId: org!.id, amount }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallet", org?.id] });
    },
  });
}

export function useTopUpStatus(topUpId: string | undefined) {
  const org = useCurrentOrganization();

  return useQuery({
    queryKey: ["wallet", "topup", org?.id, topUpId],
    queryFn: () => getTopUpStatus(org!.id, topUpId!),
    enabled: !!org?.id && !!topUpId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling once terminal
      if (status === "Paid" || status === "Failed" || status === "Cancelled")
        return false;
      return 3000;
    },
  });
}
